import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useXFetch } from '~/shared/utils/useXFetch';
import {
  getConfiguration,
  getConfigurationDefaults,
  configurationQueryKey,
} from '~/settings/components/ConfigurationsPage/helpers/configurationApi';
import { transformConfigurationResponse } from '~/settings/components/ConfigurationsPage/helpers/transformConfigResponse';

import type { ConfigurationLevel } from '~/settings/components/ConfigurationsPage/helpers/configurationApi';
import type { ConfigRecord, ConfigTreeNode } from '../types';

interface UseConfigurationQueryParams {
  nodeId: string | null;
  nodeName: string | null;
  nodeType: string | null;
  level?: string;
  clusterId?: string;
  instanceId?: string;
  region?: string;
  accountId?: string;
}

export function useConfigurationQuery({
  nodeId,
  nodeName,
  nodeType,
  level,
  clusterId,
  instanceId,
  region,
  accountId,
}: UseConfigurationQueryParams) {
  const api = useXFetch();
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () =>
      configurationQueryKey({
        nodeId,
        level: level as ConfigurationLevel | undefined,
        clusterId,
        instanceId,
        region: region || 'us-east-1',
        accountId,
      }),
    [nodeId, level, clusterId, instanceId, region, accountId]
  );

  return useQuery<ConfigRecord | null>({
    queryKey,
    queryFn: async () => {
      // console.log('[Loading] queryKey:', queryKey);
      if (!nodeId || !level) {
        return null;
      }

      // Validate required parameters based on level
      if (level === 'cluster' && !clusterId) {
        console.warn('clusterId is required for cluster level configurations');
        return null;
      }
      if (level === 'instance' && !instanceId) {
        console.warn('instanceId is required for instance level configurations');
        return null;
      }

      // Fetch both with and without overrides in parallel
      const params = {
        level,
        clusterId: clusterId || undefined,
        instanceId: instanceId || undefined,
        region: region || undefined,
        accountId: accountId || undefined,
        includeOverrides: true as const,
      };

      const [responseWithOverrides, defaults] = await Promise.all([
        getConfiguration(api, { ...params, level: level as ConfigurationLevel }),
        getConfigurationDefaults(api, {
          level: level as ConfigurationLevel,
          clusterId: clusterId || undefined,
          instanceId: instanceId || undefined,
          region: region || undefined,
          accountId: accountId || undefined,
        }),
      ]);

      const result = transformConfigurationResponse(
        responseWithOverrides,
        defaults,
        nodeId,
        nodeName || nodeId,
        (nodeType as ConfigTreeNode['type']) || 'instance'
      );

      // Preserve lastUpdated from cache if API returns empty/undefined
      // This handles cases where the GET endpoint doesn't return the timestamp
      // but we have it cached from a previous successful edit/update.
      if (!result.lastUpdated) {
        const previousData = queryClient.getQueryData<ConfigRecord | null>(queryKey);
        if (previousData?.lastUpdated) {
          result.lastUpdated = previousData.lastUpdated;
        }
      }

      return result;
    },
    enabled:
      !!nodeId &&
      !!level &&
      (level !== 'cluster' || !!clusterId) &&
      (level !== 'instance' || !!instanceId),
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
  });
}
